import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, ArrowLeft, Edit, Save, X, Plus, CreditCard, Coins, Check, ToggleLeft, ToggleRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function AdminSubscriptionPlans() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan({
      ...plan,
      features: plan.features?.join(", ") || ""
    });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingPlan) return;
    
    setSaving(true);
    try {
      const payload = {
        plan_id: editingPlan.plan_id,
        name: editingPlan.name,
        price: parseFloat(editingPlan.price),
        credits: parseInt(editingPlan.credits),
        priority: parseInt(editingPlan.priority),
        features: editingPlan.features.split(",").map(f => f.trim()).filter(f => f),
        description: editingPlan.description || "",
        active: editingPlan.active
      };
      
      await axios.put(`${API}/admin/subscription-plans/${editingPlan.plan_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Plan "${editingPlan.name}" updated`);
      setShowEditDialog(false);
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (plan) => {
    try {
      const payload = {
        ...plan,
        active: !plan.active
      };
      
      await axios.put(`${API}/admin/subscription-plans/${plan.plan_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Plan ${!plan.active ? 'activated' : 'deactivated'}`);
      fetchPlans();
    } catch (error) {
      toast.error("Failed to toggle plan");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-subscription-plans">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-5 h-5 mr-2" /> Back to Admin
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <h1 className="font-manrope font-bold text-xl text-[#1F2933]">Subscription Plans</h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-[#1F2933]/60">
            Manage subscription plans. Changes affect new subscriptions immediately and existing subscriptions on next billing cycle.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {['starter', 'pro', 'elite']
              .map(planId => plans.find(p => p.plan_id === planId))
              .filter(Boolean)
              .map((plan) => {
              const costPerCredit = plan.credits > 0 ? (plan.price / plan.credits).toFixed(2) : 0;
              return (
              <div 
                key={plan.plan_id}
                className={`bg-white rounded-2xl p-6 border-2 transition-all ${
                  plan.active ? 'border-gray-100' : 'border-dashed border-gray-300 opacity-60'
                }`}
                data-testid={`plan-card-${plan.plan_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-manrope font-bold text-xl text-[#1F2933]">{plan.name}</h3>
                    <Badge className={plan.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                      {plan.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <span className="text-xs text-[#1F2933]/50">Priority: {plan.priority}</span>
                </div>
                
                <div className="mb-4">
                  <span className="text-4xl font-bold text-[#1F2933]">${plan.price}</span>
                  <span className="text-[#1F2933]/50">/month</span>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-[#1F2933]/60 mb-1">Monthly Credits</p>
                  <p className="text-2xl font-bold text-[#0FA3B1] flex items-center gap-2">
                    <Coins className="w-5 h-5" /> {plan.credits}
                  </p>
                  <p className="text-xs text-[#1F2933]/50 mt-1">${costPerCredit} / credit</p>
                </div>
                
                {plan.features && plan.features.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-[#1F2933]/70">
                        <Check className="w-4 h-4 text-[#7ED957] flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(plan)}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(plan)}
                  >
                    {plan.active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Plan: {editingPlan?.name}</DialogTitle>
          </DialogHeader>
          
          {editingPlan && (
            <div className="space-y-4 pt-4">
              <div>
                <Label>Plan Name</Label>
                <Input
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monthly Price ($)</Label>
                  <Input
                    type="number"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({...editingPlan, price: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Credits/Month</Label>
                  <Input
                    type="number"
                    value={editingPlan.credits}
                    onChange={(e) => setEditingPlan({...editingPlan, credits: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Priority (higher = better placement)</Label>
                <Input
                  type="number"
                  value={editingPlan.priority}
                  onChange={(e) => setEditingPlan({...editingPlan, priority: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Features (comma-separated)</Label>
                <Textarea
                  value={editingPlan.features}
                  onChange={(e) => setEditingPlan({...editingPlan, features: e.target.value})}
                  placeholder="Access to leads, Priority placement, etc."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Input
                  value={editingPlan.description || ""}
                  onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-[#0FA3B1]" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <div className="spinner w-4 h-4 border-2 border-white/20 border-t-white"></div> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
